// src/modules/app-views/goodsReceipt/pages/Document/index.jsx
// *******************************************************************************
// GOODS RECEIPT DOCUMENT PAGE
// *******************************************************************************
// Main page for creating and editing goods receipt documents (NF-e entries).
// Contains tabs: Record, Payment, Transport, Fiscal, NfeInfo.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Tabs } from 'antd';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import useFormInstance from 'hooks/useFormInstance';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import ActionsFloatButton from 'components/JarvislyModule/ActionsFloatButton';
import goodsReceiptDocumentFloatButton from './actions/floatButton';
import { confirmGoodsReceipt, cancelGoodsReceipt } from 'modules/app-views/goodsReceipt/service';
import appService from 'services/appService';
import {
  FormOutlined,
  DollarOutlined,
  CarOutlined,
  AuditOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import GoodsReceiptRecordTab from './tabs/RecordTab';
import GoodsReceiptPaymentTab from './tabs/PaymentTab';
import GoodsReceiptTransportTab from './tabs/TransportTab';
import GoodsReceiptFiscalTab from './tabs/FiscalTab';
import GoodsReceiptNfeInfoTab from './tabs/NfeInfoTab';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const GoodsReceiptDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode, refresh, registerTabFieldPrefixes } =
    useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef } = useFormInstance({ data });
  const storedLanguage = useZustand('theme', 'language');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const requiredFields = ['documentNumber', 'issueDate'];
  const isNewDocument = viewMode === 'add';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [items, setItems] = useState([]);
  const [installments, setInstallments] = useState([]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!initTab) return;
    registerTabFieldPrefixes?.({ '': initTab });
  }, [registerTabFieldPrefixes, initTab]);

  // Initialize items and installments when data changes
  useEffect(() => {
    setItems(data?.items || []);
    setInstallments(data?.payment?.installments || []);
  }, [data]);

  // Initialize issuer form field from hydrated entity (same pattern as invoices)
  useEffect(() => {
    if (isNewDocument || !form) return;

    // Get issuer entity from hydration pipeline or saved snapshot
    const issuerEntity = data?.issuerEntity;
    const issuerSnapshot = data?.issuer;
    const issuerId = data?.issuerId || issuerSnapshot?.entityId || issuerEntity?._id;

    if (issuerId) {
      form.setFieldsValue({ issuerId });
    }

    // Initialize carrier form field
    const carrierSnapshot = data?.logistics?.carrier;
    const carrierEntity = data?.carrierEntity;
    const carrierId = carrierSnapshot?.entityId || carrierEntity?._id;

    if (carrierId) {
      form.setFieldsValue({ carrierId });
    }
  }, [isNewDocument, data?.issuerEntity, data?.issuer, data?.issuerId, data?.logistics?.carrier, data?.carrierEntity, form]);

  // Build tabs whenever relevant dependencies change
  useEffect(() => {
    const tabsLocal = buildTabsArray({
      ...props,
      initTab,
      viewMode,
      isNewDocument,
      data,
      items,
      setItems,
      installments,
      setInstallments,
    });
    buildTabs(tabsLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, data, items, installments, storedLanguage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // Confirm goods receipt entry - triggers side effects (stock, payables, etc.)
  const handleConfirm = useCallback(async () => {
    const receiptId = data?._id;
    if (!receiptId) return;

    const result = await confirmGoodsReceipt(receiptId);
    if (result) {
      appService.message({ flag: 'success', text: 'confirm_entry' });
      // Confirm may create/update products with new unitUids — refresh parameters
      // so product pages resolve unit names correctly after navigation.
      try {
        const { useAppStore } = await import('stores/app');
        const refreshFn = useAppStore.getState().refreshParameters;
        if (typeof refreshFn === 'function') await refreshFn();
      } catch (_e) {
        // non-fatal
      }
      refresh?.();
    }
  }, [data, refresh]);

  // Cancel confirmed goods receipt - reverses side effects
  const handleCancel = useCallback(async () => {
    const receiptId = data?._id;
    if (!receiptId) return;

    const result = await cancelGoodsReceipt(receiptId);
    if (result) {
      appService.message({ flag: 'success', text: 'cancel_entry' });
      refresh?.();
    }
  }, [data, refresh]);

  const handleFinish = useCallback(
    async ({ values, allValues }) => {
      const receiptId = allValues?._id || data?._id;

      // Build issuer snapshot from entity data (same pattern as invoices)
      const issuerSnapshot = buildIssuerSnapshot({
        formIssuer: allValues?.issuer,
        savedIssuer: data?.issuer,
        issuerEntity: data?.issuerEntity,
      });

      // Build carrier snapshot from form values + entity data
      const carrierSnapshot = buildCarrierSnapshot({
        formCarrier: allValues?.carrier,
        savedCarrier: data?.logistics?.carrier,
        carrierEntity: data?.carrierEntity,
      });

      const dataToSave = {
        ...allValues,
        // Issuer snapshot with all required fields
        issuer: issuerSnapshot,
        issuerId: issuerSnapshot?.entityId || allValues?.issuerId,
        // Items with sequential numbering
        items: items.map((item, index) => ({ ...item, itemNumber: index + 1 })),
        // Carrier snapshot under logistics
        logistics: {
          ...(allValues?.logistics || {}),
          carrier: carrierSnapshot,
        },
        // Installments under payment
        payment: {
          ...(allValues?.payment || {}),
          installments,
          // Preserve existing methods (read-only, from XML import)
          methods: data?.payment?.methods || allValues?.payment?.methods || [],
        },
      };

      // Clean carrierId (not a top-level backend field)
      delete dataToSave.carrierId;
      // Clean carrier (was form-level, now moved under logistics)
      delete dataToSave.carrier;

      await upsert({ dataSet: dataToSave, _id: receiptId });

      // Refresh to get hydrated data (issuerEntity from aggregation pipeline)
      // The PUT response doesn't include hydrated fields, only the raw document.
      // IMPORTANT: Only refresh for existing documents (PUT). For new documents (POST),
      // post() already calls get() internally with the correct _id. Calling refresh()
      // after POST would use the stale hookRef._id ('add') which fails isMongoObjectId()
      // validation and triggers abortUnauthorizedDocument.
      const isExistingDoc = receiptId && receiptId !== 'add';
      if (isExistingDoc) {
        refresh?.();
      }

      return true;
    },
    [data, items, installments, upsert, refresh]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // Build float actions based on current document status
  const floatActions = goodsReceiptDocumentFloatButton({
    data,
    handlers: {
      onConfirm: handleConfirm,
      onCancel: handleCancel,
    },
  });

  const initialValues = {
    status: 'draft',
    origin: 'manual',
    ...(data || {}),
  };

  const tabsItems = useMemo(
    () =>
      (availableTabs || []).map(t => ({
        key: t.key,
        label: t.label,
        children: t.children,
        disabled: t.disabled,
        forceRender: t.forceRender,
      })),
    [availableTabs]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          initialValues={initialValues}
          requiredFields={requiredFields}
          locked={viewMode === 'view'}
          showSubmitButton={false}
          showInfoMessage={false}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div className="position-relative">
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.issuer?.tradeName || data?.issuer?.name}
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>

      {floatActions.length > 0 && <ActionsFloatButton floatActions={floatActions} />}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default GoodsReceiptDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, isNewDocument } = props;

  return [
    {
      key: initTab,
      label: (
        <span className="tab-icon-label">
          <FormOutlined />
          {translateX('record')}
        </span>
      ),
      disabled: false,
      children: (
        <GoodsReceiptRecordTab
          {...props}
          tabName={initTab}
          defaultOpenNfe={false}
          defaultOpenIssuer={false}
          defaultOpenItems={false}
        />
      ),
    },
    {
      key: 'payment',
      label: (
        <span className="tab-icon-label">
          <DollarOutlined />
          {translateX('payment')}
        </span>
      ),
      disabled: isNewDocument,
      children: <GoodsReceiptPaymentTab {...props} tabName="payment" />,
    },
    {
      key: 'transport',
      label: (
        <span className="tab-icon-label">
          <CarOutlined />
          {translateX('transport')}
        </span>
      ),
      disabled: isNewDocument,
      children: <GoodsReceiptTransportTab {...props} tabName="transport" />,
    },
    {
      key: 'fiscal',
      label: (
        <span className="tab-icon-label">
          <AuditOutlined />
          {translateX('fiscal')}
        </span>
      ),
      disabled: isNewDocument,
      children: <GoodsReceiptFiscalTab {...props} tabName="fiscal" />,
    },
    {
      key: 'nfeInfo',
      label: (
        <span className="tab-icon-label">
          <FileTextOutlined />
          {translateX('nfe_info')}
        </span>
      ),
      disabled: isNewDocument,
      children: <GoodsReceiptNfeInfoTab {...props} tabName="nfeInfo" />,
    },
  ];
}

function buildIssuerSnapshot({ formIssuer, savedIssuer, issuerEntity }) {
  // Priority: form values > hydrated entity > saved snapshot
  const entity = issuerEntity || {};
  const saved = savedIssuer || {};
  const form = formIssuer || {};

  const entityId = form.entityId || entity._id || saved.entityId;
  if (!entityId) return form; // No entity selected - return raw form data

  // Extract CNPJ (can be object or string)
  const cnpjValue =
    form.cnpj ||
    (typeof entity.cnpj === 'object' ? entity.cnpj?.value : entity.cnpj) ||
    saved.cpfCnpj;

  // Extract IE
  const ieValue =
    form.ie || entity.ie || entity.fiscalData?.stateRegistration || saved.stateRegistration;

  // Extract tax regime
  const taxRegime = entity.fiscalData?.taxRegime || saved.taxRegime || 'simples_nacional';
  const taxRegimeCode = saved.taxRegimeCode || convertTaxRegimeToCrt(taxRegime);

  // Build address
  const sourceAddress = entity.address || saved.address || {};
  const addressData = {
    zip: sourceAddress.zip || sourceAddress.zipCode,
    address1: sourceAddress.address1 || sourceAddress.street,
    address2: sourceAddress.address2 || sourceAddress.complement,
    number: sourceAddress.number || 'S/N',
    neighborhood: sourceAddress.neighborhood || sourceAddress.district,
    city: sourceAddress.city,
    cityCode: sourceAddress.cityCode,
    region: sourceAddress.region || sourceAddress.state,
    countryCode: sourceAddress.countryCode || 'BR',
    country: sourceAddress.country || 'Brasil',
  };

  return {
    entityId,
    cpfCnpj: cnpjValue,
    name: form.name || entity.razaoSocial || entity.name || saved.name,
    tradeName: form.tradeName || entity.nomeFantasia || entity.tradeName || saved.tradeName,
    stateRegistration: ieValue,
    taxRegime,
    taxRegimeCode,
    email: form.email || saved.email,
    phone: form.phone || saved.phone,
    address: addressData,
  };
}

/**
 * Build carrier snapshot for persistence under logistics.carrier.
 * Same priority pattern as buildIssuerSnapshot: form > entity > saved.
 */
function buildCarrierSnapshot({ formCarrier, savedCarrier, carrierEntity }) {
  const form = formCarrier || {};
  const entity = carrierEntity || {};
  const saved = savedCarrier || {};

  const entityId = form.entityId || entity._id || saved.entityId;
  if (!entityId) return form.entityId ? form : undefined;

  const cnpjValue =
    form.cpfCnpj ||
    (typeof entity.cnpj === 'object' ? entity.cnpj?.value : entity.cnpj) ||
    entity.uniqueKey ||
    saved.cpfCnpj;

  const ieValue =
    form.stateRegistration || entity.fiscalData?.stateRegistration || entity.ie || saved.stateRegistration;

  const sourceAddress = entity.address || entity.addresses?.[0] || saved.address || {};
  const addressData = {
    address1: sourceAddress.address1 || sourceAddress.street,
    number: sourceAddress.number,
    neighborhood: sourceAddress.neighborhood,
    city: sourceAddress.city,
    region: sourceAddress.region || sourceAddress.state,
    zip: sourceAddress.zip || sourceAddress.zipCode,
    countryCode: sourceAddress.countryCode || 'BR',
  };

  return {
    entityId,
    cpfCnpj: cnpjValue,
    name: form.name || entity.razaoSocial || entity.name || saved.name,
    tradeName: form.tradeName || entity.nomeFantasia || entity.tradeName || saved.tradeName,
    stateRegistration: ieValue,
    phone: form.phone || saved.phone,
    address: addressData,
  };
}

/**
 * Convert taxRegime string to CRT code (same as invoices helper)
 */
function convertTaxRegimeToCrt(taxRegime) {
  if (typeof taxRegime === 'number') return taxRegime;
  if (typeof taxRegime === 'string') {
    const regime = taxRegime.toLowerCase();
    if (regime.includes('mei')) return 4;
    if (regime.includes('simples') && regime.includes('excess')) return 2;
    if (regime.includes('simples')) return 1;
    if (regime.includes('normal') || regime.includes('lucro')) return 3;
  }
  return 1;
}
